import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, PenToolIcon, BarChart2Icon, TrophyIcon, ArrowRightIcon } from 'lucide-react';
const steps = [{
  number: '1',
  title: 'Meet your AI Creative Strategist',
  body: 'Pulsewave analyzes your content, audience conversations, recurring requests, and creator context to surface ideas that fit you.',
  accent: 'Find what your fans are already asking for.',
  icon: SparklesIcon,
  tone: 'electric' as const
}, {
  number: '2',
  title: 'Shape the idea',
  body: 'Turn the strongest opportunities into clear directions for content, offers, communities, experiences, or launches.',
  accent: 'Move from raw signal to a real creative direction.',
  icon: PenToolIcon,
  tone: 'mint' as const
}, {
  number: '3',
  title: 'Validate with Pulse Markets',
  body: 'Put real options in front of fans and see which direction earns the most conviction.',
  accent: 'Let your audience weigh the path.',
  icon: BarChart2Icon,
  tone: 'electric' as const
}, {
  number: '4',
  title: 'Build momentum and reward sharp fans',
  body: 'Turn the process into something fans follow, then recognize the people who consistently help you get closer to the right answer.',
  accent: 'Build interest before it ships — and reward the fans who help shape it.',
  icon: TrophyIcon,
  tone: 'mint' as const
}];
export function ProductLoop() {
  return <section id="how-it-works" className="py-24 px-6 relative bg-navy-950 border-t border-white/5 scroll-mt-20" data-id="element-100">
      <div className="max-w-5xl mx-auto" data-id="element-101">
        <div className="text-center mb-14" data-id="element-102">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-semibold tracking-tight mb-4 text-white" data-id="element-103">
            How Pulsewave works
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-104">
            Start with your audience. End with a direction you can act on.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-105">
          {steps.map((step, i) => {
          const Icon = step.icon;
          const isMint = step.tone === 'mint';
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className="bg-navy-900/40 border border-white/5 rounded-3xl p-6 sm:p-7 flex flex-col h-full" data-id="element-106">
                <div className="flex items-start gap-4 mb-5" data-id="element-107">
                  <span className="text-4xl font-semibold text-slate-700 leading-none shrink-0 w-10" data-id="element-108">
                    {step.number}
                  </span>
                  <div className="flex-1" data-id="element-109">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${isMint ? 'bg-mint/10' : 'bg-electric/10'}`} data-id="element-110">
                      <Icon className={`w-5 h-5 ${isMint ? 'text-mint' : 'text-electric'}`} data-id="element-111" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2" data-id="element-112">
                      {step.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed mb-2" data-id="element-113">
                      {step.body}
                    </p>
                    <p className={`text-sm font-medium ${isMint ? 'text-mint' : 'text-electric'}`} data-id="element-114">
                      {step.accent}
                    </p>
                  </div>
                </div>

                <div className="mt-auto pt-1" data-id="element-115">
                  {i === 0 && <StrategistCard data-id="element-116" />}
                  {i === 1 && <ShapeCard data-id="element-117" />}
                  {i === 2 && <ValidateCard data-id="element-118" />}
                  {i === 3 && <MomentumCard data-id="element-119" />}
                </div>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-14 text-center" data-id="element-120">
          <p className="text-2xl font-semibold text-white" data-id="element-121">
            Every cycle makes the next move smarter.
          </p>
        </motion.div>
      </div>
    </section>;
}
function StrategistCard() {
  return <div className="bg-navy-950 rounded-2xl p-5 border border-white/5" data-id="element-122">
      <div className="text-[11px] text-slate-500 mb-2.5 uppercase tracking-wider" data-id="element-123">
        Inputs
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-124">
        {['content', 'comments', 'fan requests', 'creator context'].map((item, i) => <span key={i} className="bg-navy-800/60 rounded-full px-2.5 py-1 text-[11px] text-slate-400 border border-white/5" data-id="element-125">
              {item}
            </span>)}
      </div>
      <div className="flex items-center gap-2 text-slate-500 text-[11px] mb-2 uppercase tracking-wider" data-id="element-126">
        Output
      </div>
      <div className="bg-electric/10 rounded-lg px-3 py-2.5 text-sm text-white flex items-center gap-2 border border-electric/20" data-id="element-127">
        <SparklesIcon className="w-4 h-4 text-electric shrink-0" data-id="element-128" />
        High-fit idea
      </div>
    </div>;
}
function ShapeCard() {
  return <div className="bg-navy-950 rounded-2xl p-5 border border-white/5" data-id="element-129">
      <div className="flex items-center gap-2 mb-4" data-id="element-130">
        <span className="bg-navy-800/60 rounded-full px-3 py-1.5 text-[11px] text-slate-400 border border-white/5" data-id="element-131">
          Raw signals
        </span>
        <ArrowRightIcon className="w-3.5 h-3.5 text-slate-600 shrink-0" data-id="element-132" />
        <span className="bg-mint/10 rounded-full px-3 py-1.5 text-[11px] text-mint border border-mint/20" data-id="element-133">
          Creative direction
        </span>
      </div>
      <div className="space-y-1.5" data-id="element-134">
        {['Limited workshop series', 'Fan-requested merch drop', 'New recurring content format'].map((item, i) => <div key={i} className="bg-navy-800/40 rounded-lg px-3 py-2 text-xs text-slate-300 border border-white/5" data-id="element-135">
            {item}
          </div>)}
      </div>
    </div>;
}
function ValidateCard() {
  return <div className="bg-navy-950 rounded-2xl p-5 border border-white/5" data-id="element-136">
      <div className="text-[11px] text-slate-500 mb-3 uppercase tracking-wider" data-id="element-137">
        Pulse Market
      </div>
      <div className="mb-3" data-id="element-138">
        <div className="text-[11px] text-slate-400" data-id="element-139">Goal</div>
        <div className="text-sm text-white font-medium" data-id="element-140">
          Choose the next project
        </div>
      </div>
      <div className="space-y-1.5" data-id="element-141">
        {['New series', 'Workshop', 'Private community'].map((item, i) => <div key={i} className="flex items-center justify-between bg-navy-800/40 rounded-lg px-3 py-2 border border-white/5" data-id="element-142">
            <span className="text-xs text-slate-300" data-id="element-143">{item}</span>
            <div className="h-1.5 w-16 rounded-full bg-navy-700 overflow-hidden" data-id="element-144">
              <div className="h-full bg-electric" style={{
            width: `${[42, 78, 30][i]}%`
          }} data-id="element-145" />
            </div>
          </div>)}
      </div>
    </div>;
}
function MomentumCard() {
  return <div className="bg-navy-950 rounded-2xl p-5 border border-white/5" data-id="element-146">
      <div className="flex items-center justify-between mb-3" data-id="element-147">
        <span className="text-[11px] text-electric font-medium" data-id="element-148">
          Interest building
        </span>
        <span className="flex items-center gap-1.5 text-[11px] text-mint font-medium" data-id="element-149">
          <span className="w-1.5 h-1.5 rounded-full bg-mint animate-pulse" data-id="element-150" />
          Live
        </span>
      </div>
      <div className="bg-navy-800/40 rounded-lg px-3 py-2.5 mb-3 border border-white/5" data-id="element-151">
        <div className="text-[10px] text-slate-500 mb-0.5 uppercase tracking-wider" data-id="element-152">
          Top take
        </div>
        <div className="text-xs text-white" data-id="element-153">
          "The workshop is the move — I'd pay for it today."
        </div>
      </div>
      <div className="flex items-center justify-between" data-id="element-154">
        <span className="text-[11px] text-slate-400" data-id="element-155">Sharpest fans</span>
        <span className="bg-mint/10 rounded-full px-2.5 py-1 text-[10px] text-mint border border-mint/20" data-id="element-156">
          Rewards
        </span>
      </div>
    </div>;
}